/* eslint-disable react/prop-types */
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { FixedSizeList as List } from 'react-window';

import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import { journalTracker } from '../../../../shared/utils/amplitudeHelper';
import { setJournalTab, setSelectedAthlete } from '../../../journal/ducks/journalActions';

// Matches the athlete SelectBox styling from the Mobile Preview sidebar
// verbatim (PhoneSideBarContent's customSelectStyles).
const customSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    fontFamily: 'Nunito Sans',
    fontSize: '16px',
    height: '44px',
    fontWeight: 400,
    background: 'white',
    border: '2px solid #EEEEEE',
    boxShadow: 'none',
    '&:hover': {
      borderColor: state.isFocused ? '#EEEEEE' : '#EEEEEE',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    padding: '4px',
    borderRadius: '4px',
    backgroundColor: 'white',
    color: state.isSelected ? '#BDBDBD' : '#444444',
    fontFamily: 'Nunito Sans',
    fontSize: '16px',
    marginBottom: '8px',
    fontWeight: 400,
    '&:hover': {
      backgroundColor: '#FCFCFC',
    },
  }),
  menu: (provided) => ({
    ...provided,
    border: '1px solid #EEEEEE',
    borderRadius: '8px',
    minWidth: '250px',
    padding: '16px',
    marginTop: '0px',
    marginBottom: '0px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: '#9E9E9E',
  }),
  input: (provided, state) => ({
    ...provided,
    color: state.isDisabled ? 'inherit' : provided.color,
  }),
};

const Wrapper = styled('div')`
  width: 100%;
`;

// Fixed row height for the windowed athlete list below — plain avatar-less
// text rows, so (unlike Mobile Preview's variable-height MenuList) there's no
// need to measure and correct each row's real height after mount. That
// measure-then-resize dance shifts row positions right after the menu opens,
// which is exactly the kind of moving target that can eat a click landing
// mid-correction — a fixed height sidesteps the whole class of bug.
const OPTION_ROW_HEIGHT = 44;

// Windowed menu list for the athlete SelectBox so the full-roster option list
// doesn't render every row at once (react-select's default) — same problem
// AthletePickerModal's own athlete list already solves this way.
const MenuList = ({ children, innerRef, maxHeight }) => {
  // react-select's blur handler only keeps the menu open on an option click
  // if menuListRef.contains(document.activeElement) — menuListRef comes from
  // this innerRef. Without forwarding it to an element that actually
  // contains the rendered rows, every option click blurs the hidden input,
  // the menu closes before the click lands, and selecting anything silently
  // no-ops.
  if (!children?.length) {
    // No matches: children is react-select's single
    // NoOptionsMessage node (no .length) — render as-is.
    return <div ref={innerRef}>{children}</div>;
  }
  const height = Math.min(children.length * OPTION_ROW_HEIGHT, maxHeight);

  return (
    <div ref={innerRef}>
      <List
        height={height}
        itemCount={children.length}
        itemSize={OPTION_ROW_HEIGHT}
        width='100%'
      >
        {({ index, style }) => (
          <div style={style}>
            {children[index]}
          </div>
        )}
      </List>
    </div>
  );
};

const MemoizedMenuList = React.memo(MenuList);

const getAthleteFullName = (athlete) => `${athlete.first || ''} ${athlete.last || ''}`.trim();

/**
 * Inline, searchable athlete select for the Build journal sidebar — replaces
 * the athlete-picker-modal flow used standalone, modeled on the Mobile
 * Preview sidebar's own athlete SelectBox (PhoneSideBarContent).
 */
const JournalAthleteSelect = ({ scrollToTop }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const athletes = useSelector((state) => state.journal.data.allAthletes);
  const currentAthlete = useSelector((state) => state.journal.data.currentAthlete);

  const options = useMemo(() => {
    const myJournal = {
      first: 'My',
      id: currentUser?.id,
      last: 'Journal',
      name: 'My Journal',
      pic: currentUser?.pic,
    };
    const roster = Array.isArray(athletes) ? athletes : [];
    return [myJournal, ...roster].map((athlete) => ({
      ...athlete,
      name: athlete.name || getAthleteFullName(athlete),
    }));
  }, [athletes, currentUser?.id, currentUser?.pic]);

  const selectedOption = useMemo(
    () => options.find((athlete) => athlete.id === currentAthlete?.id) || null,
    [options, currentAthlete?.id],
  );

  const handleChange = (athlete) => {
    if (!athlete) return;
    const selectedMyJournal = athlete.id === currentUser?.id;
    dispatch(setJournalTab('SINGLE_ATHLETE'));
    dispatch(setSelectedAthlete(athlete));
    if (scrollToTop) scrollToTop();
    journalTracker('Build sidebar', `${selectedMyJournal ? 'My Journal' : 'Athlete'} selected`);
  };

  return (
    <Wrapper>
      <SelectBox
        options={options}
        value={selectedOption}
        onChangeFunction={handleChange}
        isClearable={false}
        placeholder='Search athlete by name'
        customStyles={customSelectStyles}
        menuListComponent={MemoizedMenuList}
        styledProps={{ height: '' }}
      />
    </Wrapper>
  );
};

JournalAthleteSelect.propTypes = {
  scrollToTop: PropTypes.func.isRequired,
};

export default JournalAthleteSelect;
